/* eslint-disable max-lines */
import React, { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import Tabs from 'components/Tabs'
import Search from 'components/Search'
import ResourceTable from './ResourceTable'
import AddButton from 'components/AddButton'
import Select from 'components/Select'

import ResourceFormModal from 'pages/ResourceAdmin/ResourceFormModal'
import FieldTechHelp from 'pages/shared/FieldTechHelp'
import ProductLiteratureSearch from 'pages/shared/ProductLiterature'

import useAutoFocus from 'hooks/useAutoFocus'
import useResources from 'hooks/useResources'
import useProductLiterature from 'hooks/useProductLiterature'
import useFthVideos from 'hooks/useFthVideos'

import { screenSize, PageHeader, PageTitle } from 'styles/shared'

import Resource, { ValidResourceTypes, EditableResourceProps } from 'types/Resource'

type ResourceProps = {
  type: ValidResourceTypes
  resource: Resource
}

const BRAND_OPTIONS = [[
  { value: 'trane_home', name: 'Trane' },
  { value: 'asair_home', name: 'American Standard' }
]]

const TAB_CONFIG = [
  { route: 'field-tech-help', type: 'field-tech-help', header: 'Field Tech Help' },
  { route: 'product-literature', type: 'product-literature', header: 'Product Literature' },
  { route: 'videos', type: 'video', header: 'Videos' },
  { route: 'articles', type: 'article', header: 'Articles' },
  { route: 'faqs', type: 'faq', header: 'FAQs' },
  { route: 'thermostat-releases', type: 'thermostat', header: 'Thermostat Releases' }
] as const

type RouteTab = (typeof TAB_CONFIG)[number]['route']
const TABS = TAB_CONFIG.map(t => t.route) as readonly RouteTab[]

const ResourceAdmin = () => {
  const navigate = useNavigate()
  const { tab } = useParams<{ tab?: string }>()
  const routeTab: RouteTab = (tab && (TABS as readonly string[]).includes(tab)) ? (tab as RouteTab) : TABS[0]
  const currentConfig = TAB_CONFIG.find(t => t.route === routeTab) ?? TAB_CONFIG[0]
  const currentTab: ValidResourceTypes = currentConfig.type
  const editableTab = (['video', 'article', 'faq', 'thermostat'] as const).includes(currentTab as 'video' | 'article' | 'faq' | 'thermostat')
    ? (currentTab as 'video' | 'article' | 'faq' | 'thermostat')
    : 'video'
  const activeTabIndex = TABS.indexOf(routeTab)
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())
  const [selectedBrand, setSelectedBrand] = useState('trane_home')
  const [searchValue, setSearchValue] = useState('')
  const [submittedSearchValue, setSubmittedSearchValue] = useState('')
  const { productLiterature, isProductLiteratureLoaded, serverError: plServerError } = useProductLiterature(submittedSearchValue)
  const { fthVideos, isFthLoading } = useFthVideos(submittedSearchValue)
  const [isResourceFormModalDisplayed, setIsResourceFormModalDisplayed] = useState(false)
  const { thermostatReleases, articles, faqs, videos, areResourcesLoaded } = useResources({ brand: selectedBrand, publishedOnly: false, refreshTimestamp })
  const [isEdit, setIsEdit] = useState(false)
  const [editableResource, setEditableResource] = useState<EditableResourceProps>({
    incomingType: currentTab,
    incomingResource: null
  })
  const title = useRef(null)
  useAutoFocus({ element: title })

  const onSuccessfulSubmission = () => {
    setRefreshTimestamp(Date.now())
    setIsEdit(false)
    setEditableResource({
      incomingType: currentTab,
      incomingResource: null
    })
  }

  const onCloseCallback = () => {
    setIsEdit(false)
    setIsResourceFormModalDisplayed(false)
    setEditableResource({
      incomingType: currentTab,
      incomingResource: null
    })
  }

  const onClickAddResource = (e:React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    setEditableResource({
      incomingType: currentTab,
      incomingResource: null
    })
    setIsEdit(false)
    setIsResourceFormModalDisplayed(true)
  }

  const onClickEditResource = (props:ResourceProps) => {
    const { resource, type } = props || {}
    setEditableResource({
      incomingType: type,
      incomingResource: resource as Resource
    })
    setIsEdit(true)
    setIsResourceFormModalDisplayed(true)
  }

  const onSearchClicked = () => {
    setSubmittedSearchValue(searchValue)
  }

  const onClickClearIconClicked = () => {
    setSearchValue('')
    setSubmittedSearchValue('')
  }

  const handleTabChange = (index: number) => {
    const tabName = TABS[index] || TABS[0]
    navigate(`/admin/resources/${tabName}`)
  }

  return (
    <S.Container>
      <S.PageHeader>
        <PageTitle ref={title}>Resources</PageTitle>
        <S.Search
          value={searchValue}
          placeholder="Search"
          ariaLabel="resources search results"
          onChange={setSearchValue}
          onSearch={onSearchClicked}
          onClickClearIcon={onClickClearIconClicked}
        />
      </S.PageHeader>

      <S.SinglePanePage>
        <S.ControlsRow>
          <S.Select
            label="Brand"
            selectOptions={BRAND_OPTIONS}
            value={selectedBrand}
            onChange={(e) => { setSelectedBrand(e.target.value) }}
          />

          <S.AddButton
            uri="/admin/resources"
            onClick={onClickAddResource}
          >
            Add Resource
          </S.AddButton>
        </S.ControlsRow>
        {isResourceFormModalDisplayed &&
          <ResourceFormModal
            isDisplayed={isResourceFormModalDisplayed}
            closeCallback={onCloseCallback}
            onSuccessfulSubmission={onSuccessfulSubmission}
            isEdit={isEdit}
            editableResource={editableResource}
            activeTab={editableTab}
          />}

        <Tabs
          activeTabIndex={activeTabIndex}
          onSelectTabIndexCallback={handleTabChange}
          tabItems={[
            {
              key: '0',
              header: 'Field Tech Help',
              details: <FieldTechHelp searchedValue={submittedSearchValue} videos={fthVideos} isLoading={isFthLoading} />
            },
            {
              key: '1',
              header: 'Product Literature',
              details: <ProductLiteratureSearch searchedValue={submittedSearchValue} productLiterature={productLiterature} isProductLiteratureLoaded={isProductLiteratureLoaded} serverError={plServerError} />
            },
            {
              key: '2',
              header: 'Videos',
              details: (
                <ResourceTable
                  type="video"
                  resources={videos}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            },
            {
              key: '3',
              header: 'Articles',
              details: (
                <ResourceTable
                  type="article"
                  resources={articles}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            },
            {
              key: '4',
              header: 'FAQs',
              details: (
                <ResourceTable
                  type="faq"
                  resources={faqs}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            },
            {
              key: '5',
              header: <S.Header>Thermostat Releases</S.Header>,
              details: (
                <ResourceTable
                  type="thermostat"
                  resources={thermostatReleases}
                  isLoaded={areResourcesLoaded}
                  onSuccessfulSubmission={onSuccessfulSubmission}
                  onClickEditResource={onClickEditResource}
                />
              )
            }
          ]}
        />
      </S.SinglePanePage>
    </S.Container>
  )
}

const S = {
  Container: styled.div`
    width: 100%;
  `,
  PageHeader: styled(PageHeader)`
  `,
  Search: styled(Search)`
    max-width: 500px;

    @media ${screenSize.small} {
      display: block;
      width: 100%;
    }
  `,
  SinglePanePage: styled.div`
    margin: 0px 32px 24px 24px;
    flex: 1;
    min-height: 500px;

    @media ${screenSize.medium} {
      margin: 0px 5px 24px;
    }

    @media ${screenSize.small} {
      padding: 15px;
      margin: 15px;
    }
  `,
  AddButton: styled(AddButton)`
    margin: 0;
  `,
  Select: styled(Select)`
    margin-bottom: 0;
    width: 200px;
  `,
  ControlsRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;

    @media ${screenSize.small} {
      flex-direction: column;
      align-items: stretch;

      > * {
        width: 100%;
      }
    }
  `,
  Header: styled.div`
    padding: 0 15px;
  `
}

export default ResourceAdmin
